import React, { useState, useRef } from 'react';
import { Button } from "./ui/button";
import { X, Download, FileText, ZoomIn, ZoomOut, RefreshCw, RotateCcw } from "lucide-react";
import { downloadAuthenticatedMedia, useAuthenticatedMediaUrl } from './AuthenticatedMedia';

/**
 * AttachmentPreviewModal
 * - Accepts an `attachment` object with optional fields:
 *   { type, name, size, lastModified, src, preview_url, url }
 * - Supports image, video, pdf, text. Falls back to download for unknown types.
 */
const AttachmentPreviewModal = ({ attachment, onClose }) => {
  const rawSrc = attachment?.src || attachment?.preview_url || attachment?.url || '';
  const { url: authenticatedSrc, loading: mediaLoading, error: mediaError } = useAuthenticatedMediaUrl(rawSrc);
  if (!attachment) return null;

  // normalize and detect types (including MS Office / Excel / Word)
  const type = (attachment.type || '').toLowerCase();
  // Prefer explicit src then preview_url/url (lowercase for checks)
  const src = authenticatedSrc || '';
  const lowerSrc = (src || '').toLowerCase();
  const name = (attachment.name || '').toLowerCase();
  const getExt = (s) => {
    if (!s) return '';
    try {
      const cleaned = s.split('?')[0].split('#')[0];
      const parts = cleaned.split('.');
      return parts.length > 1 ? parts.pop().toLowerCase() : '';
    } catch {
      return '';
    }
  };
  const ext = getExt(name) || getExt(lowerSrc);

  const isImage = type.startsWith('image/') ||
    ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg', 'avif'].includes(ext) ||
    lowerSrc.startsWith('data:image/');

  const isVideo = type.startsWith('video/') ||
    ['mp4', 'webm', 'ogg', 'mov', 'mkv'].includes(ext) ||
    lowerSrc.startsWith('data:video/');

  const isPDF = type.includes('pdf') || ext === 'pdf' || lowerSrc.endsWith('.pdf');

  const isText = type.startsWith('text/') || ['txt', 'log', 'md', 'json', 'csv'].includes(ext);

  const officeRegex = /msword|vnd\.ms-excel|vnd\.openxmlformats-officedocument|officedocument|presentation|spreadsheet|excel|sheet|wordprocessingml/i;
  const isOfficeDoc = officeRegex.test(type) || ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext);

  const handleDownload = async () => {
    try {
      await downloadAuthenticatedMedia(rawSrc, attachment.name || '');
    } catch (err) {
      console.error('Download failed', err);
    }
  };

  // For office docs hosted on a public URL, use Google Docs viewer to embed previews
  const googleDocsViewer = (url) => `https://docs.google.com/gview?url=${encodeURIComponent(url)}&embedded=true`;

  /**
   * ImageWithZoom
   * - Lightweight image preview with zoom in / zoom out / rotate / reset controls
   * - Supports smooth drag-to-pan on both desktop and mobile
   * - Ensures the modal itself doesn't change sizes when zooming or rotating
   */
  const ImageWithZoom = ({ src, alt, onError }) => {
    const [scale, setScale] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const dragStart = useRef({ x: 0, y: 0 });

    const min = 0.25;
    const max = 5;
    const step = 0.25;

    const zoomIn = () => setScale(s => Math.min(max, +(s + step).toFixed(2)));
    const zoomOut = () => setScale(s => Math.max(min, +(s - step).toFixed(2)));
    const rotate = () => setRotation(r => (r + 90) % 360);
    const reset = () => {
      setScale(1);
      setRotation(0);
      setPosition({ x: 0, y: 0 });
    };

    const handleMouseDown = (e) => {
      e.preventDefault();
      setIsDragging(true);
      dragStart.current = { x: e.clientX - position.x, y: e.clientY - position.y };
    };

    const handleMouseMove = (e) => {
      if (!isDragging) return;
      setPosition({
        x: e.clientX - dragStart.current.x,
        y: e.clientY - dragStart.current.y
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    // Touch events for mobile/tablet panning support
    const handleTouchStart = (e) => {
      if (e.touches.length !== 1) return;
      setIsDragging(true);
      const touch = e.touches[0];
      dragStart.current = { x: touch.clientX - position.x, y: touch.clientY - position.y };
    };

    const handleTouchMove = (e) => {
      if (!isDragging || e.touches.length !== 1) return;
      const touch = e.touches[0];
      setPosition({
        x: touch.clientX - dragStart.current.x,
        y: touch.clientY - dragStart.current.y
      });
    };

    return (
      <div
        className="relative w-full h-full flex items-center justify-center overflow-hidden select-none bg-gray-50 dark:bg-gray-900 rounded-lg"
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleMouseUp}
      >
        {/* Floating Image Controls Panel */}
        <div className="absolute bottom-5 right-5 z-20 flex items-center bg-white/90 dark:bg-gray-800/90 rounded-full p-1.5 shadow-lg border border-gray-200 dark:border-gray-700 gap-1">
          <Button
            size="sm"
            variant="ghost"
            className="rounded-full h-8 w-8 p-0"
            onClick={zoomOut}
            title="Zoom out"
          >
            <ZoomOut className="h-4 w-4 text-gray-700 dark:text-gray-200" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="rounded-full h-8 w-8 p-0"
            onClick={reset}
            title="Reset view"
          >
            <RotateCcw className="h-4 w-4 text-gray-700 dark:text-gray-200" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="rounded-full h-8 w-8 p-0"
            onClick={rotate}
            title="Rotate clockwise"
          >
            <RefreshCw className="h-4 w-4 text-gray-700 dark:text-gray-200" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="rounded-full h-8 w-8 p-0"
            onClick={zoomIn}
            title="Zoom in"
          >
            <ZoomIn className="h-4 w-4 text-gray-700 dark:text-gray-200" />
          </Button>
        </div>

        {/* Drag Container */}
        <div
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          style={{
            transform: `translate(${position.x}px, ${position.y}px)`,
            cursor: isDragging ? 'grabbing' : 'grab',
            transition: isDragging ? 'none' : 'transform 100ms ease-out',
          }}
          className="flex items-center justify-center w-full h-full"
        >
          <img
            src={src}
            alt={alt}
            onError={onError}
            style={{
              transform: `scale(${scale}) rotate(${rotation}deg)`,
              transition: 'transform 150ms ease-out',
              maxWidth: '90%',
              maxHeight: '90%',
              objectFit: 'contain',
              userSelect: 'none',
              pointerEvents: 'none' // Disables native ghost dragging
            }}
            className="shadow-sm rounded"
          />
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-[60] flex items-center justify-center p-4">
      {/* Click overlay to close */}
      <div className="absolute inset-0" onClick={onClose} />

      <div className={`relative bg-white rounded-lg flex flex-col overflow-hidden ${isPDF || isOfficeDoc ? 'w-full max-w-7xl h-[85vh]' : 'w-full max-w-4xl h-[75vh]'}`}>

        {/* Floating header controls (Close and optional Download) */}
        <div className="absolute top-4 right-4 z-50 flex items-center gap-2">
          {isOfficeDoc && !attachment.isObjectURL && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              className="bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-700 shadow-md border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200"
            >
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          )}
          <button
            onClick={onClose}
            className="p-2 bg-white/80 dark:bg-gray-800/80 rounded-full hover:bg-white dark:hover:bg-gray-700 shadow-md transition-colors border border-gray-200 dark:border-gray-700"
            title="Close preview"
          >
            <X className="h-5 w-5 text-gray-700 dark:text-gray-200" />
          </button>
        </div>

        {/* Content Area */}
        <div className="flex-1 w-full overflow-hidden bg-gray-50 flex items-center justify-center">
          {mediaLoading ? (
            <div className="text-sm text-gray-500">Loading protected attachment…</div>
          ) : mediaError ? (
            <div className="text-center text-gray-500 p-8">
              <FileText className="h-16 w-16 mx-auto mb-4 text-gray-400" />
              <p className="font-medium text-gray-700">Attachment unavailable</p>
            </div>
          ) : isImage ? (
            <ImageWithZoom
              src={src}
              alt={attachment.name}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          ) : isVideo ? (
            <div className="p-4 w-full h-full flex items-center justify-center">
              <video
                src={src}
                controls
                className="max-w-full max-h-full object-contain bg-black rounded shadow"
              >
                Your browser does not support the video tag.
              </video>
            </div>
          ) : isPDF ? (
            <iframe
              src={src}
              className="w-full h-full border-0"
              title={attachment.name || 'PDF preview'}
            />
          ) : isOfficeDoc ? (
            (src && (src.startsWith('http://') || src.startsWith('https://'))) ? (
              <iframe
                src={googleDocsViewer(src)}
                className="w-full h-full border-0"
                title={attachment.name || 'Document preview'}
              />
            ) : (
              <div className="text-center text-gray-500 p-8">
                <FileText className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                <p className="font-medium mb-2 text-gray-700">Preview not available for this document</p>
                <p className="text-sm mb-4 text-gray-500">Download to view the document.</p>
                <Button onClick={handleDownload} className="bg-indigo-600 hover:bg-indigo-700">
                  <Download className="h-4 w-4 mr-2" />
                  Download File
                </Button>
              </div>
            )
          ) : isText ? (
            <div className="bg-white p-6 rounded border w-[90%] max-h-[90%] overflow-auto shadow-sm">
              <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono">
                Text file preview - content must be loaded from the server.
              </pre>
            </div>
          ) : (
            <div className="text-center text-gray-500 p-8">
              <FileText className="h-16 w-16 mx-auto mb-4 text-gray-400" />
              <p className="font-medium mb-2 text-gray-700">Preview not available</p>
              <p className="text-sm mb-4 text-gray-500">
                This file type ({attachment.type || 'unknown'}) cannot be previewed here.
              </p>
              <Button onClick={handleDownload} className="bg-indigo-600 hover:bg-indigo-700">
                <Download className="h-4 w-4 mr-2" />
                Download File
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttachmentPreviewModal;
